import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Box } from "@mui/material";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import {
  mapToTransactionCertificateReqDto,
  transactionCertificateReqDto,
} from "../../tc-models/tc-req-dto/transaction-certificate.req_dto";
import { dtrackItService } from "../../tc-service/dtrackit.service";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useFormActions } from "../../../../shared/hooks/form-builder.hooks";
import { useApi } from "../../../../shared/hooks/useApi";
import { LoaderComponent } from "../../../../shared/components/loaders/page-loader";
import useNotification from "../../../../shared/hooks/useNotification";
import {
  TC_CREATE_ROUTE_CONFIG,
  TCAppConfig,
} from "../../../../shared/app-configs/tc-app-configs";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { Utills } from "../../../../shared/app-configs/utilit";
import AlertPopup from "../../../../shared/components/modals/alert-moadal";
import { RestrictedDataValidation } from "../../../../shared/utils/custom-validation.utils";

const dateSchema = Yup.string()
  .required("This field is required")
  .matches(
    /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
    "Date must be in DD/MM/YYYY format"
  );

interface TransactionCertificateProps {
  onNextTab: () => Promise<void>;
}
const TransactionCertificate: React.FC<TransactionCertificateProps> = ({
  onNextTab,
}) => {
  // const getTCDetails = transactionCertificateGetService.transactionCertsGetStore((state) => state.getTCDetails);
  // const tcDetails = transactionCertificateGetService.transactionCertsGetStore((state) => state.tcDetails);
  const [organicFarmStandardsData, setOrganicFarmStandards] = useState([{}]);
  const { TE_ID, formtype } = useParams();
  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTC: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";
  const { notify, showBanner } = useNotification();
  const navigate = useNavigate();
  const { getCurrentFormValues, updateField } = useFormActions(
    "Transaction-Details"
  );

  const { loading, error, executeGet, executePost } = useApi();
  const [initialValues, setInitalValues] = useState(
    transactionCertificateReqDto
  );
  const [existingData, setExistingData] = useState(
    transactionCertificateReqDto
  );
  const [isVersionUpdated, setIsVersionUpdated] = React.useState(false);

  const [getZustandData, setZustandData] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popUpTitle, setPopUpTitle] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [popUpBtnName, setPopUpBtnName] = useState("");

  // State management for country and state dropdowns
  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');

  const formRef = useRef<any>(null);
  const {
    setDelayReason,
    setTCIssueDate,
    getEditTCInfo,
    setLabelGrades,
    cbLicensingCode,
    setTCStandardGOTS,
    getTCStandardGOTS,
    setOrgFarmStandards,
    setEditTCInfo,
  } = useTransactionCertificateStore();

  const getOrganicFarmStandards = async () => {
    try {
      const result: any = await dtrackItService.getOrganicFarmStandards();
      const orgFStd = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setOrganicFarmStandards(orgFStd);
      setOrgFarmStandards(orgFStd);
    } catch (err) {
      Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`);
    }
  };

  // Fetch countries
  const getCountries = async () => {
    try {
      const result = await dtrackItService.getCountry();
      const countries = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setCountryOptions(countries);
    } catch (err) {
      notify('Failed to fetch countries', 'error');
    }
  };

  // Fetch states based on selected country
  const getStates = async (countryCode: string) => {
    try {
      const countryParam = JSON.stringify(`[${[countryCode].map((item) => `'${item}'`)}]`).replace(/^"|"$/g, '');
      const result = await dtrackItService.getStateBasedonCountry(countryParam);
      const states = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
        description: t.description_1,
      }));
      setStateOptions(states);
    } catch (err) {
      setStateOptions([]);
      notify('Failed to fetch states', 'error');
    }
  };

  // Split Place of Issue into State and Country
  const splitPlaceOfIssue = async (placeOfIssue: string) => {
    if (!placeOfIssue) return;
    
    const parts = placeOfIssue.split(',').map(part => part.trim());
    
    if (parts.length === 2) {
      const [stateDescription, countryIdentifier] = parts;

      let matchingCountry = countryOptions.find(c => c.value === countryIdentifier);
      if (!matchingCountry) {
        matchingCountry = countryOptions.find(c => 
          c.label.toLowerCase().startsWith(countryIdentifier.toLowerCase())
        );
      }
      
      const countryCode = matchingCountry?.value || countryIdentifier;
      setSelectedCountry(countryCode);
      
      if (countryCode) {
        try {
          const countryParam = JSON.stringify(`[${[countryCode].map((item) => `'${item}'`)}]`).replace(/^"|"$/g, '');
          const result = await dtrackItService.getStateBasedonCountry(countryParam);
          const states = result.data.map((t: any) => ({
            value: t.code_1,
            label: `${t.description_1} (${t.code_1})`,
            description: t.description_1,
          }));
          setStateOptions(states);
          
          const matchingState = states.find((s: any) => 
            s.description.toLowerCase() === stateDescription.toLowerCase()
          );
          
          if (matchingState) {
            setSelectedState(matchingState.value);
            formRef.current?.setFieldValue('tcPlaceOfIssueCountry', countryCode);
            formRef.current?.setFieldValue('tcPlaceOfIssueState', matchingState.value);
            
            const countryName = matchingCountry?.label?.split(' (')[0] || countryCode;
            formRef.current?.setFieldValue('tcPlaceOfIssue', `${matchingState.description}, ${countryName}`);
          } else {
            setSelectedState(stateDescription);
            formRef.current?.setFieldValue('tcPlaceOfIssueCountry', countryCode);
            formRef.current?.setFieldValue('tcPlaceOfIssueState', stateDescription);

            const countryName = matchingCountry?.label?.split(' (')[0] || countryCode;
            formRef.current?.setFieldValue('tcPlaceOfIssue', `${stateDescription}, ${countryName}`);
          }
        } catch (err) {
          setStateOptions([]);
          notify('Failed to load state information', 'error');
        }
      }
    } else {
      setSelectedCountry('');
      setSelectedState('');
      formRef.current?.setFieldValue('tcPlaceOfIssueCountry', '');
      formRef.current?.setFieldValue('tcPlaceOfIssueState', '');
    }
  };

  const combinePlaceOfIssue = (state: string, country: string): string => {
    if (state && country) {
      const selectedCountryObj = countryOptions.find(c => c.value === country);
      const countryName = selectedCountryObj?.label?.split(' (')[0] || country;
      return `${state}, ${countryName}`;
    }
    return '';
  };
  const isGotsExists = async (tcStandard: string[]): Promise<boolean> => {
    return tcStandard.length === 1 && tcStandard.includes("GOTS");
  };
  const fetchTcBaseDetails = async () => {
    try {
      const response: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS, {
        tcId: TE_ID,
      });
      const data = mapToTransactionCertificateReqDto(response.data);
      data.tcCbLicensingCode = cbLicensingCode;

      await getStandardLabelGrade([...data?.tcStandard, "NA"]);
      let gotsValue = await isGotsExists(data.tcStandard);
      setTCStandardGOTS(gotsValue);
      setTCIssueDate(data?.tcDateOfIssue ?? "");
      setDelayReason(data?.tcDelayReason === "" ? false : true);
      setInitalValues(data);
      setExistingData(data);
    } catch (err) {
      console.error("Failed to fetch TC Base Details:", err);
    }
  };

  const fetchTcBaseDetailsforSubmitFlow = async (tcid: any) => {
    try {
      const response: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS, {
        tcId: tcid,
      });
      const data = mapToTransactionCertificateReqDto(response.data);
      data.tcCbLicensingCode = cbLicensingCode;
      setTCIssueDate(data?.tcDateOfIssue ?? "");
      setDelayReason(data?.tcDelayReason === "" ? false : true);
      setInitalValues(data);
    } catch (err) {
      console.error("Failed to fetch TC Base Details:", err);
    }
  };
  const setZustandInitialState = (initialState?: any) => {
    const defalutState: any = initialValues;
    //set zustand state inital values
    Object.entries(initialState ?? defalutState).forEach(([key, value]) => {
      updateField(key, value);
    });
  };
  useEffect(() => {
    var currentState: any = getCurrentFormValues();
    if (Object.keys(currentState).length === 0) {
      setZustandInitialState(null);
    } else {
      setZustandInitialState(currentState);
      setInitalValues(currentState);
    }
  }, [getZustandData]);

  const getStandardLabelGrade = async (value: any) => {
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_STANDARD_LABEL_GRADE_BY_TCSTANDARD,
        { standard: JSON.stringify(value) }
      );
      if (response.status == 200) {
        const labelGrades = response.data.map((item: any) => ({
          label: item.description_1,
          value: item.code_1,
        }));
        setLabelGrades(labelGrades);
      }
    } catch (err) {
      console.error("Failed to fetch StandardLabelGrade:", err);
    }
  };

  useEffect(() => {
    if (TE_ID) fetchTcBaseDetails();
    const fetchData = async () => {
      await getOrganicFarmStandards();
      await getCountries();
    };
    fetchData();
  }, []);

  // Parse tcPlaceOfIssue when initialValues changes
  useEffect(() => {
    const loadPlaceOfIssue = async () => {
      // Wait for countries to be loaded before processing
      if (countryOptions.length === 0) return;
      
      if (initialValues.tcPlaceOfIssueCountry && initialValues.tcPlaceOfIssueState) {
        setSelectedCountry(initialValues.tcPlaceOfIssueCountry);
        setSelectedState(initialValues.tcPlaceOfIssueState);
        await getStates(initialValues.tcPlaceOfIssueCountry);
      } else if (initialValues.tcPlaceOfIssue) {
        await splitPlaceOfIssue(initialValues.tcPlaceOfIssue);
      }
    };
    
    loadPlaceOfIssue();
  }, [initialValues.tcPlaceOfIssue, initialValues.tcPlaceOfIssueCountry, initialValues.tcPlaceOfIssueState, countryOptions]);
  const transactionCertificateValidationService = {
    transactioncertsValidation: Yup.object().shape({
      tcNo: Yup.string().required("Transaction Certificate No. is required"),
      tcStandard: Yup.array()
        .min(1, "Select at least one TC Standard")
        .required("TC Standard is required"),
      tcDateOfIssue: Yup.date()
        .required("TC Date of Issue is required")
        .test(
          "date-of-issue-validation",
          "TC cannot be issued after the TC is last updated",
          function (value) {
            const { tcLastUpdated } = this.parent;
            if (!value || !tcLastUpdated) return true;
            return (
              new Date(value).getTime() <= new Date(tcLastUpdated).getTime()
            );
          }
        ),
      tcVersionNo: Yup.string().when("tcStandard", {
        is: (tcStandard: any) => !getTCStandardGOTS,
        then: Yup.string()
          .required("TC Version No. is required")
          .min(6, "Minimum 6 characters are required")
          .max(100, "Maximum 100 Characters are Allowed"),
        otherwise: Yup.string().nullable(), // Make it optional if GOTS is included
      }),
      tcStatus: Yup.string()
        .when("tcStandard", {
          is: (tcStandard: any) => !getTCStandardGOTS,
          then: Yup.string().required("TC Status is required"),
          otherwise: Yup.string().nullable(), // Make it optional if "GOTS" is included
        })
        .test(
          "tcStatus-validation",
          "Custom Error: Status A is not allowed",
          function (value) {
            const { tcNo, tcVersionNo, tcDateOfIssue, tcLastUpdated } =
              this.parent; // Accessing other fields
            if (value === "A") {
              if (tcNo === tcVersionNo) {
                return this.createError({
                  message:
                    "For TC Status Amended-Transaction Certificate No. must not be equal to TC Version No.",
                });
              }
              // if (
              //   tcDateOfIssue &&
              //   tcLastUpdated &&
              //   new Date(tcDateOfIssue).getTime() ===
              //     new Date(tcLastUpdated).getTime()
              // ) {
              //   return this.createError({
              //     message:
              //       "For TC Status Amended-TC Date of Issue must not be equal to TC Last Updated Date",
              //   });
              // }
            }
            return true; // Validation passes
          }
        ),
      tcAmendmentReason: Yup.string().when(["tcStatus", "tcStandard"], {
        is: (tcStatus: any, tcStandard: any) =>
          !getTCStandardGOTS && tcStatus === "A", // Skip if GOTS exists
        then: Yup.string().required("Amendment Reason is required"),
        otherwise: Yup.string().nullable(),
      }),
      tcInvalidationReason: Yup.string().when(["tcStatus", "tcStandard"], {
        is: (tcStatus: any, tcStandard: any) =>
          !getTCStandardGOTS && tcStatus === "I",
        then: Yup.string().required("Invalidate Reason is required"),
        otherwise: Yup.string().nullable(),
      }),
      tcPlaceOfIssueCountry: Yup.string()
        .when("tcStandard", {
          is: (tcStandard: any) => !getTCStandardGOTS,
          then: Yup.string().required("Country is required"),
          otherwise: Yup.string().nullable(),
        }),
      tcPlaceOfIssueState: Yup.string()
        .when("tcStandard", {
          is: (tcStandard: any) => !getTCStandardGOTS,
          then: Yup.string().required("State is required"),
          otherwise: Yup.string().nullable(),
        }),
      tcPlaceOfIssue: Yup.string()
        .when("tcStandard", {
          is: (tcStandard: any) => !getTCStandardGOTS, // Apply required only if "GOTS" is NOT in tcStandard
          then: Yup.string().required("TC Place of Issue is required"),
        })
        .max(255, "Maximum 255 Characters are Allowed"),

      tcLastUpdated: Yup.date().when("tcStandard", {
        is: (tcStandard: any) => !getTCStandardGOTS,
        then: Yup.date().required("TC Last Updated is required"),
      }),

      tcOrganicFarmStandard: Yup.array().nullable(),
      tcApplicationReceived: Yup.date().nullable(),
      tcApplicationComplete: Yup.date().nullable(),
      tcUsdaNopCompliantFlag: Yup.string()
        .nullable()
        .test(
          "UsdaNopCompliantFlag-required",
          "Certification of the organic material complies with USDA NOP rules is required when OCS is selected",
          function (value) {
            const { tcStandard } = this.parent;
            const isOCSSelected =
              Array.isArray(tcStandard) && tcStandard.includes("OCS");
            if (isOCSSelected && !value) {
              return this.createError({
                message:
                  "Certification of the organic material complies with USDA NOP rules is required",
              });
            }

            return true;
          }
        ),
      tcDelayReason: Yup.string().nullable(),
    }),
  };

  const formConfig = {
    pageName: "Transaction-Details",
    initialValues,
    validationSchema:
      transactionCertificateValidationService.transactioncertsValidation,
    sections: [
      {
        title: "Standards and Certifications",
        fields: [
          {
            label: "Standard",
            name: "tcStandard",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            disabled:
              (getEditTCInfo?.tcMetadataStatus === "Amending" ||
              (getEditTCInfo?.tcId === TE_ID && getEditTCInfo?.isParentStatus))
                ? true
                : false,
            readOnly: hasSubmitTC,
            events: {
              onChange: async (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                let gotsValue = await isGotsExists(updatedValue);
                // //alert(gotsValue)
                setTCStandardGOTS(gotsValue);
              },
            },
            props: {
              multiple: true,
              enableSearch: true,
              options: [
                { label: "Organic Content Standard (OCS)", value: "OCS" },
                { label: "Responsible Down Standard (RDS)", value: "RDS" },
                { label: "Responsible Alpaca Standard (RAS)", value: "RAS" },
                { label: "Global Recycled Standard (GRS)", value: "GRS" },
                { label: "Responsible Wool Standard (RWS)", value: "RWS" },
                { label: "Content Claim Standard (CCS)", value: "CCS" },
                { label: "Recycled Claim Standard (RCS)", value: "RCS" },
                { label: "Responsible Mohair Standard (RMS)", value: "RMS" },
                // { label: "Sustainable Fiber Alliance (SFA)", value: "SFA" },
                // {
                //   label: "Global Organic Textile Standard (GOTS)",
                //   value: "GOTS",
                // },
              ],
              placeholder: "Select one or more options",
            },
          },
          {
            label: "Organic Farm Standards",
            name: "tcOrganicFarmStandard",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            readOnly: hasSubmitTC,
            visible: (value: any) => value.tcStandard.includes("OCS"),
            props: {
              multiple: true,
              options: organicFarmStandardsData,
              placeholder: "Select one or more options",
            },
          },
          {
            label:
              "Certification of the organic material complies with USDA NOP rules",
            name: "tcUsdaNopCompliantFlag",
            type: "radio",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              if (getTCStandardGOTS) {
                return false;
              }
              return value.tcStandard.includes("OCS");
            },
            readOnly: hasSubmitTC,
            visible: (value: any) => value.tcStandard.includes("OCS"),
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
            },
          },
        ],
      },
      {
        title: "Transaction Certificate Details",
        fields: [
          {
            label: "Transaction Certificate No.",
            name: "tcNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            disabled:
              getEditTCInfo?.tcId === TE_ID && getEditTCInfo?.isParentStatus
                ? true
                : false,
            readOnly: hasSubmitTC,
            props: {
              placeholder: "Enter Transaction Certificate No.",
              variant: "outlined",
              fullWidth: true,
            },
            events: {
              onChange: (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                setFieldValue("tcVersionNo", updatedValue);
              },
            },
          },
          {
            label: "Version No.",
            name: "tcVersionNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              if (getTCStandardGOTS) {
                return false;
              }
              return true;
            },
            readOnly: hasSubmitTC,
            props: {
              placeholder: "Enter Version Number",
              variant: "outlined",
              fullWidth: true,
            },
            events: {
              onChange: (value: any) => {
                if (hasSubmitTC) setIsVersionUpdated(true);
              },
            },
          },
          {
            label: "Status",
            name: "tcStatus",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              if (getTCStandardGOTS) {
                return false;
              }
              return true;
            },
            readOnly: hasSubmitTC,
            props: {
              options: [
                { value: "V", label: "Valid (V)" },
                { value: "I", label: "Invalid (I)" },
                { value: "A", label: "Amended (A)" },
              ],
              placeholder: "Select Status",
              size: "small",
            },
          },
          {
            label: "Amendment Reason",
            name: "tcAmendmentReason",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              if (getTCStandardGOTS) {
                return false;
              }
              return value.tcStatus === "A";
            },
            visible: (value: any) => value.tcStatus === "A",
            readOnly: hasSubmitTC,
            props: {
              options: [
                {
                  value: "B",
                  label: "B:Buyer information - corrections to buyer name and address information",
                },
                {
                  value: "E",
                  label:
                    "E:Exemption - reasons where Textile Exchange has granted an exemption",
                },
                {
                  value: "G",
                  label: "G:Downgrading/Upgrading - downgrading from GRS to RCS or upgrading from RCS to GRS",
                },
                {
                  value:"N",
                  label:"N:Adding or correcting a TEID number or a client number"
                },
                {
                  value: "R",
                  label: "R:Reduction - reduction in the quantity of claimed material",
                },
                {
                  value: "T",
                  label:
                    "T:Typographical errors - typographical errors within fourteen calendar days of issuance",
                }
                // {
                //   value: "C",
                //   label: "C:Certification body errors",
                // },
              ],
              placeholder: "Select Amendment Reason",
              size: "small",
            },
          },
          {
            label: "Invalidation Reason",
            name: "tcInvalidationReason",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            // disabled: (value: any) => value.tcStatus === 'V' || value.tcStatus === 'A',
            required: (value: any) => {
              if (getTCStandardGOTS) {
                return false;
              }
              return value.tcStatus === "I";
            },
            visible: (value: any) => value.tcStatus === "I",
            readOnly: hasSubmitTC,
            props: {
              options: [
                { value: "F", label: "F: Falsified Or Fraudulent Documents" },
                { value: "E", label: "E: Claimed materials are/will be included in an e-transaction" },
                {
                  value: "I",
                  label: "I: Applicable Input Transaction Has Been Invalidated",
                },
                {
                  value: "M",
                  label: "M: Claimed Materials Are Shown To Be Ineligible",
                },
                { value: "S", label: "S: Requests From The Seller" },
                { value: "O", label: "O: Other Reason" },
                { value: "C", label: "C: Certification body errors" },
              ],
              placeholder: "Select Invalidation Reason",
              size: "small",
            },
          },
          {
            label: "Delay Reason",
            name: "tcDelayReason",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            readOnly: hasSubmitTC,
            props: {
              options: [
                { value: "A", label: "A: acceptable animal fiber exception" },
                {
                  value: "D",
                  label:
                    "D: acceptable transport documentation authentication exception",
                },
                {
                  value: "E",
                  label: "E: exemption issued by Textile Exchange",
                },
                {
                  value: "F",
                  label:
                    "F: acceptable sellers with final products as inputs exception",
                },
                {
                  value: "I",
                  label: "I: input transaction certificate issued late",
                },
                {
                  value: "T",
                  label: "T: products previously covered under an eTransaction",
                },
              ],
              placeholder: "Select Delay Reason",
              size: "small",
            },
          },
        ],
      },
      {
        title: "Issuance and Application Dates",
        fields: [
          {
            label: "Place of Issue Country",
            name: "tcPlaceOfIssueCountry",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              if (getTCStandardGOTS) {
                return false;
              }
              return true;
            },
            readOnly: hasSubmitTC,
            events: {
              onChange: (newValue: any) => {
                setSelectedCountry(newValue);
                setSelectedState('');
                formRef.current?.setFieldValue('tcPlaceOfIssueState', '');
                formRef.current?.setFieldValue('tcPlaceOfIssue', '');
                
                if (newValue) {
                  getStates(newValue);
                }
              },
            },
            props: {
              enableSearch: true,
              options: countryOptions,
              placeholder: "Select Country",
              fullWidth: true,
            },
          },
          {
            label: "Place of Issue State",
            name: "tcPlaceOfIssueState",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              if (getTCStandardGOTS) {
                return false;
              }
              return true;
            },
            readOnly: hasSubmitTC,
            disabled: (values: any) => !values.tcPlaceOfIssueCountry,
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                setSelectedState(newValue);
                const selectedStateObj = stateOptions.find(s => s.value === newValue);
                const stateDescription = selectedStateObj?.description || newValue;
                const combinedValue = combinePlaceOfIssue(stateDescription, updatedValues.tcPlaceOfIssueCountry);
                formRef.current?.setFieldValue('tcPlaceOfIssue', combinedValue);
              },
            },
            props: {
              enableSearch: true,
              options: stateOptions,
              placeholder: "Select State",
              fullWidth: true,
            },
          },
          {
            label: "Place of Issue",
            name: "tcPlaceOfIssue",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              if (getTCStandardGOTS) {
                return false;
              }
              return true;
            },
            readOnly: hasSubmitTC,
            props: {
              placeholder: "Auto-filled from Country and State",
              fullWidth: true,
              disabled: true,
            },
          },
          {
            label: "Date of Issue",
            name: "tcDateOfIssue",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            disabled:
              (getEditTCInfo?.tcMetadataStatus === "Amending" ||
              (getEditTCInfo?.tcId === TE_ID && getEditTCInfo?.isParentStatus))
                ? true
                : false,
            readOnly: hasSubmitTC,
            props: {
              placeholder: "Select Date of Issue",
              fullWidth: true,
            },
          },
          {
            label: "Last Updated",
            name: "tcLastUpdated",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              if (getTCStandardGOTS) {
                return false;
              }
              return true;
            },
            readOnly: hasSubmitTC,
            props: {
              placeholder: "Select Last Updated Date",
              fullWidth: true,
            },
          },
          {
            label: "Application Receive",
            name: "tcApplicationReceived",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            readOnly: hasSubmitTC,
            props: {
              placeholder: "Select Application Received Date",
              fullWidth: true,
            },
          },
          {
            label: "Application Complete",
            name: "tcApplicationComplete",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            readOnly: hasSubmitTC,
            props: {
              placeholder: "Select Application Complete Date",
              fullWidth: true,
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      try {
        const transactionCertsData = mapToTransactionCertificateReqDto(
          values,
          TE_ID,
          isVersionUpdated
        );
        transactionCertsData.tcCbLicensingCode = cbLicensingCode;
        const isTcNoChanged = existingData.tcNo !== transactionCertsData.tcNo;
        const isVersionNoChanged =
          existingData.tcVersionNo !== transactionCertsData.tcVersionNo;
        const queryParams = new URLSearchParams();
      
        if (TE_ID) {
          queryParams.set("isNewlyCreated", "No");
          if (isTcNoChanged || isVersionNoChanged) {
            queryParams.set("oldTcNo", existingData.tcNo);
            queryParams.set("oldTcVersionNo", existingData.tcVersionNo);
          }
        }
        else {
          queryParams.set("isNewlyCreated", "Yes");
        }
        const finalUrl = `${TCAppConfig.TRANSACTION_CERTIFICATE_ENDPOINT}?${queryParams}`;
        const result: any = await executePost(finalUrl, transactionCertsData);
        if (result.status == 200) {
          setTCIssueDate(transactionCertsData?.tcDateOfIssue ?? "");
          setDelayReason(
            transactionCertsData?.tcDelayReason === "" ? false : true
          );
          let gotsValue = await isGotsExists(transactionCertsData?.tcStandard);
          setTCStandardGOTS(gotsValue);
          await getStandardLabelGrade([...transactionCertsData?.tcStandard,"NA"]);
          notify("Data Saved Successfully", "success");
          await onNextTab();
          if (hasSubmitTC) {
            if (!isVersionUpdated) {
              navigate(
                `/tc/tc-details/seller-buyer-products/${result.data.tcId}`
              );
            } else {
              navigate(
                `/tc/tc-details/transaction-certificate/${result.data.tcId}`
              );
              fetchTcBaseDetailsforSubmitFlow(result.data.tcId);
              setIsVersionUpdated(false);
            }
          } else {
            navigate(
              `/tc/${formtype}/seller-buyer-products/${result.data.tcId}`
            );
          }
        } else {
          notify("Unable to Save", "error");
        }
        Utills.cl(`response handle submit ${JSON.stringify(result)}`);
      } catch (error) {
        console.error("Error saving Transaction Certificate:", error);
      }
    },
  };

  // Handle form submission outside of FormBuilder
  const handleSubmit = async () => {
    console.log("Submitting form...", formRef);
    
    if (formRef.current) {
      try {
        await formRef.current.setTouched({
          tcDateOfIssue: true,
          tcVersionNo: true,
          tcStatus: true,
          tcPlaceOfIssueCountry: true,
          tcPlaceOfIssueState: true,
          tcPlaceOfIssue: true,
          tcLastUpdated: true,
        });
        
        // Validate the form
        const errors = await formRef.current.validateForm();
        
        if (Object.keys(errors).length > 0) {
          console.error("Validation errors:", errors);
          notify('Please fill all the mandatory fields', 'error');
          return;
        }
        
        // If validation passes, proceed with form submission
        if (hasSubmitTC) {
          if (!isVersionUpdated) {
            navigate(`/tc/${formtype}/seller-buyer-products/${TE_ID}`);
          } else {
            formRef.current.submitForm(); 
          }
        } else {
          formRef.current.submitForm(); 
        }
      } catch (error) {
        console.error("Error during form validation:", error);
        notify('An error occurred during form validation', 'error');
      }
    }
  };
  const handleCancelAmendment = async () => {
    try {
      await executePost(TCAppConfig.GET_TC_CANCEL_AMEND, {
        tcId: TE_ID,
        tcCbLicensingCode: cbLicensingCode,
      });
      // if()
      notify("Cancelled Amendment Successfully", "success");
    } catch (err) {
      console.error("Failed to cancel amendment", err);
    }
    navigate(`/tc-dashboard`);
  };

  const handleCancelSave = () => {
    setDialogOpen(false);
  };

  return (
    <Box>
      {/* Render the FormBuilder component and pass formRef and onSubmit handler */}
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          <FormBuilder
            formConfig={formConfig}
            onSubmit={formConfig.handleSubmit}
            formRef={formRef}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            {getEditTCInfo?.isParentStatus && (
              <StyledButton
                variant="contained"
                sx={{ boxShadow: "none" }}
                onClick={() => {
                  setDialogOpen(true);
                  setPopUpTitle("Cancel Amendment");
                  setPopUpMessage(
                    "Are you sure you want to cancel this amendment?"
                  );
                  setPopUpBtnName("Confirm");
                }}
              >
                Cancel Amendment
              </StyledButton>
            )}
            <NavLink to="/tc-dashboard">
              <StyledButton variant="contained" sx={{ boxShadow: "none" }}>
                Home
              </StyledButton>
            </NavLink>
            <StyledButton
              variant="contained"
              sx={{ boxShadow: "none" }}
              onClick={handleSubmit}
            >
              {hasSubmitTC
                ? !isVersionUpdated
                  ? "Next"
                  : "Save"
                : "Save & Next"}
            </StyledButton>
            {/* Conditionally render the button */}
          </Box>
          <AlertPopup
            open={dialogOpen}
            title={popUpTitle}
            message={popUpMessage}
            onClose={handleCancelSave}
            onConfirm={handleCancelAmendment}
            confirmText={popUpBtnName}
            cancelText="Cancel"
          />
        </>
      )}
    </Box>
  );
};

export default TransactionCertificate;
